import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, BookOpen, CalendarDays, Phone, ChevronRight } from "lucide-react";
import { getAllArticles } from "@/data/blogArticles";

const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

export default function BlogList() {
  const articles = getAllArticles();
  const pillars = articles.filter(a => a.isPillar);
  const cluster = articles.filter(a => !a.isPillar);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Pflege-Ratgeber | Lehnen & Weinacht – Pflegeberatung & Alltagsbegleitung";
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="blog-list">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 shrink-0 overflow-hidden">
              <img src={ICON_URL} alt="Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" />
            </div>
            <div>
              <span className="text-sm font-bold text-slate-800 font-['Manrope'] block leading-tight">Lehnen & Weinacht</span>
              <span className="text-[9px] text-cyan-600 font-semibold tracking-wider uppercase">Pflegeberatung & Alltagsbegleitung</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-500 hover:text-cyan-700 transition-colors hidden sm:inline">Startseite</Link>
            <a href="/#termin-buchen" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-all active:scale-95">
              Termin buchen
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        {/* Page header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-600 bg-green-50 px-4 py-1.5 rounded-full mb-4">
            <BookOpen className="h-3.5 w-3.5" /> Pflege-Ratgeber
          </span>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Wissen rund um die Pflege
          </h1>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Verständliche Fachartikel zu Pflegeleistungen, Sozialraumbudget, Entlastungsbetrag und mehr — von unseren Pflegeberatern.
          </p>
        </div>

        {/* Pillar articles (featured) */}
        {pillars.map((pillar) => (
          <Link key={pillar.slug} to={`/blog/${pillar.slug}`} className="block bg-white rounded-2xl border border-cyan-200 hover:shadow-xl transition-all mb-6 overflow-hidden group" data-testid={`pillar-article-${pillar.slug}`}>
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold text-white bg-cyan-700 px-3 py-1.5 rounded-full">Hauptartikel</span>
                <span className="text-xs font-medium text-cyan-700 bg-cyan-50 px-3 py-1.5 rounded-full">{pillar.category}</span>
                <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" /> {pillar.readTime}</span>
              </div>
              <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-3 group-hover:text-cyan-700 transition-colors">
                {pillar.title}
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-5 max-w-3xl">{pillar.intro}</p>
              <span className="inline-flex items-center gap-2 text-cyan-700 font-semibold text-base group-hover:gap-3 transition-all">
                Artikel lesen <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        ))}

        {/* Cluster articles grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cluster.map((article) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="bg-white rounded-2xl border border-slate-200/60 p-6 hover:shadow-lg hover:-translate-y-1 transition-all group"
              data-testid={`article-card-${article.slug}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-cyan-700 bg-cyan-50 px-2.5 py-1 rounded-full">{article.category}</span>
                <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" /> {article.readTime}</span>
              </div>
              <h3 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-2 group-hover:text-cyan-700 transition-colors leading-snug">
                {article.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-3">{article.intro}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 group-hover:gap-2 transition-all">
                Weiterlesen <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 bg-gradient-to-br from-cyan-50 to-green-50 rounded-2xl border border-cyan-100 p-8 text-center">
          <h2 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-3">Persönliche Beratung gewünscht?</h2>
          <p className="text-base text-slate-600 mb-6 max-w-xl mx-auto">Unsere Pflegeberater helfen Ihnen, Leistungen richtig einzuordnen — kostenfrei und bequem per Video oder Telefon.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/#termin-buchen" className="inline-flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]">
              <CalendarDays className="h-5 w-5" /> Termin buchen
            </a>
            <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]">
              <Phone className="h-5 w-5" /> 0163 791 7044
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex justify-center gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
            <a href="/#kontakt" className="hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
